import type { ICaseFlightDelay, IFlightEscalation } from '@/lib/delay/types';
import type { IDisruptionFlightStatus } from '@/lib/disruptions/types';

/**
 * The escalation of a delayed flight to a cancellation, as a pure model (flight-delay
 * U5, M2). When a `DELAYED` flight is cancelled, cases moves it to `ESCALATED_GRACE`
 * and gives the airline a policy window (`graceUntil`) to reassign its passengers
 * itself; at `graceUntil` the groups the airline did not move go to Nexa's rebooking
 * (`ESCALATED_RELEASED`, `flight-delay.tds.md` §15). No language here.
 *
 * The countdown is not computed here against a clock of its own: the component passes
 * `now` from `components/allocation/use-now.ts` (a render tick, never a poll).
 */

export interface IEscalationCounts {
  reassigned: number;
  released: number;
  conflicts: number;
}

export function escalationCounts(e: Pick<IFlightEscalation, 'reassignedSubCaseUrns' | 'releasedSubCaseUrns' | 'conflictSubCaseUrns'>): IEscalationCounts {
  return {
    reassigned: e.reassignedSubCaseUrns?.length ?? 0,
    released: e.releasedSubCaseUrns?.length ?? 0,
    conflicts: e.conflictSubCaseUrns?.length ?? 0,
  };
}

/** The flight is inside the airline's window: escalated, not released yet. */
export function isInGrace<T extends Pick<ICaseFlightDelay, 'phase' | 'escalation'>>(
  d: T,
): d is T & { escalation: IFlightEscalation } {
  return d.phase === 'ESCALATED_GRACE' && !!d.escalation && !d.escalation.releasedAt;
}

/** The window is over and the release ran (the counts are final). */
export function isReleased<T extends Pick<ICaseFlightDelay, 'phase' | 'escalation'>>(
  d: T,
): d is T & { escalation: IFlightEscalation } {
  return !!d.escalation && (d.phase === 'ESCALATED_RELEASED' || !!d.escalation.releasedAt);
}

/** One flight's escalation as its block shows it, or `null` (never escalated — the empty state). */
export type CaseEscalation =
  | { state: 'grace'; escalation: IFlightEscalation }
  | { state: 'released'; escalation: IFlightEscalation; counts: IEscalationCounts };

export function caseEscalationOf(d: Pick<ICaseFlightDelay, 'phase' | 'escalation'>): CaseEscalation | null {
  if (isInGrace(d)) return { state: 'grace', escalation: d.escalation };
  if (isReleased(d)) return { state: 'released', escalation: d.escalation, counts: escalationCounts(d.escalation) };
  return null;
}

/**
 * The disruption header's alert:
 *   grace    — at least one flight is inside the airline's window: the EARLIEST
 *              `graceUntil` (the one the operator must watch), how many flights, and
 *              the conflicts recorded so far;
 *   released — every escalated flight was released: the summed counts (reassigned by
 *              the airline / sent to Nexa's rebooking / conflicts);
 *   pending  — the row's `flightStatus@v1` says the flight escalated, but only the
 *              timelines can say more (their read is loading or failed).
 * `null` — nothing escalated: the alert renders nothing.
 *
 * The timelines win when they know an escalation; before they answer, the row's
 * `graceUntil` (the earliest open window, kept by the same WS merge) already drives the
 * countdown, so the alert never flashes empty while the read runs.
 */
export type EscalationAlert =
  | { mode: 'grace'; graceUntil: string; originIata?: string; flights: number; conflicts: number }
  | ({ mode: 'released'; flights: number; releasedAt?: string; originIata?: string } & IEscalationCounts)
  | { mode: 'pending' };

export function escalationAlertOf(
  status: IDisruptionFlightStatus | null | undefined,
  delays: readonly ICaseFlightDelay[] | undefined,
): EscalationAlert | null {
  const list = delays ?? [];
  const grace = list.filter(isInGrace);
  if (grace.length > 0) {
    const lead = [...grace].sort((a, b) => Date.parse(a.escalation.graceUntil) - Date.parse(b.escalation.graceUntil))[0]!;
    return {
      mode: 'grace',
      graceUntil: lead.escalation.graceUntil,
      ...(lead.originIata ? { originIata: lead.originIata } : {}),
      flights: grace.length,
      conflicts: grace.reduce((n, d) => n + (d.escalation.conflictSubCaseUrns?.length ?? 0), 0),
    };
  }
  const released = list.filter(isReleased);
  if (released.length > 0) {
    const counts = released.reduce<IEscalationCounts>(
      (acc, d) => {
        const c = escalationCounts(d.escalation);
        return { reassigned: acc.reassigned + c.reassigned, released: acc.released + c.released, conflicts: acc.conflicts + c.conflicts };
      },
      { reassigned: 0, released: 0, conflicts: 0 },
    );
    const last = [...released].sort(
      (a, b) => Date.parse(b.escalation.releasedAt ?? '') - Date.parse(a.escalation.releasedAt ?? ''),
    )[0]!;
    return {
      mode: 'released',
      flights: released.length,
      ...counts,
      ...(last.escalation.releasedAt ? { releasedAt: last.escalation.releasedAt } : {}),
      ...(last.originIata ? { originIata: last.originIata } : {}),
    };
  }
  if (status?.escalated && status.graceUntil && Number.isFinite(Date.parse(status.graceUntil))) {
    return { mode: 'grace', graceUntil: status.graceUntil, flights: 1, conflicts: 0 };
  }
  if (status?.escalated && delays === undefined) return { mode: 'pending' };
  return null;
}

/** The disruption's escalated flights (grace first, earliest window first), for the details view. */
export function escalatedDelays(delays: readonly ICaseFlightDelay[] | undefined): ICaseFlightDelay[] {
  return (delays ?? [])
    .filter((d) => isInGrace(d) || isReleased(d))
    .sort((a, b) => {
      const ga = isInGrace(a) ? 0 : 1;
      const gb = isInGrace(b) ? 0 : 1;
      return ga - gb || Date.parse(a.escalation?.graceUntil ?? '') - Date.parse(b.escalation?.graceUntil ?? '');
    });
}
